import type { CSSProperties, ReactElement, ReactNode } from 'react';
import type { PageProps } from '../../types';
import type { ResolvedSceneNode } from '../../../../../src/build-engines/studio/types.js';

/**
 * Everything a node renderer needs beyond its own `ResolvedSceneNode`: the
 * page's proposal/section/actions/readOnly, exactly like every other
 * momentum_v2 archetype receives via `PageProps`.
 */
export interface StudioNodeContext extends Pick<PageProps, 'proposal' | 'section' | 'actions' | 'readOnly'> {}

export interface StudioNodeRendererProps {
  node: ResolvedSceneNode;
  ctx: StudioNodeContext;
}

/** CSS colour for a node's `props.tone` — momentum_v2 tokens only, no new palette. */
export function toneColor(tone: string | undefined): string | undefined {
  switch (tone) {
    case 'ink':
      return 'var(--doc-ink)';
    case 'muted':
      return 'var(--doc-muted)';
    case 'accent':
      return 'var(--mv2-lime)';
    case 'inverse':
      return 'var(--mv2-dark-text)';
    default:
      return undefined;
  }
}

/**
 * The one positioning primitive every visual node renders through: absolute
 * canvas px (1920x1080 space, see `ResolvedSceneNode.box`'s own doc comment)
 * placed inside `StudioPage`'s 1920x1080 root — never re-scaled here, the
 * ancestor `.page-canvas` transform (`Page.tsx`) does that for every surface
 * (grid/gallery/share/print) uniformly.
 */
export function StudioBox({
  node,
  className,
  style,
  children,
}: {
  node: ResolvedSceneNode;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}): ReactElement {
  const { box, z, fontSize } = node;
  return (
    <div
      className={`tpl-studio-node tpl-studio-node-${node.type}${className ? ` ${className}` : ''}`}
      data-node-id={node.id}
      data-node-type={node.type}
      style={{
        position: 'absolute',
        left: box.x,
        top: box.y,
        width: box.w,
        height: box.h,
        zIndex: z,
        ...(fontSize ? { fontSize } : null),
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/**
 * `import.meta.env.DEV` without pulling in `vite/client` ambient types
 * project-wide (this stream's own concern only — no other file in `web/`
 * references `import.meta.env`, so adding the reference here rather than to
 * `tsconfig.json` keeps the change scoped to Stream C's files).
 */
const isDev = Boolean((import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV);

/** Dev-only: an unsupported/unknown node type renders nothing but this warning. */
export function warnUnsupportedNode(node: ResolvedSceneNode): void {
  if (isDev) {
    // eslint-disable-next-line no-console
    console.warn(`[studio] unsupported scene node type "${node.type}" (id=${node.id})`);
  }
}
